import { useMutation } from '@tanstack/react-query'
import {
  Building2,
  Check,
  ChevronDown,
  CircleHelp,
  LogOut,
  Menu,
  Moon,
  Plus,
  Search,
  Settings,
  Sun,
  User,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { useEffect } from 'react'

import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/uiStore'
import { authApi } from '@/features/auth/api/authApi'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { ROLE_LABELS } from '@/features/organizations/roleLabels'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { toast } from '@/components/ui/toast'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'
import { Breadcrumbs, type BreadcrumbItem } from './PageHeader'

export interface TopbarProps {
  breadcrumbs: BreadcrumbItem[]
  className?: string
}

export function Topbar({ breadcrumbs, className }: TopbarProps) {
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen)
  const setCommandPaletteOpen = useUiStore((state) => state.setCommandPaletteOpen)

  // Cmd/Ctrl + K åbner global søgning. Genvejen registreres ét sted, så den
  // virker uanset hvilken side der er åben.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setCommandPaletteOpen(true)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [setCommandPaletteOpen])

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 px-4 lg:px-6',
        'border-b border-line-subtle bg-app-950/80 backdrop-blur-xl',
        className,
      )}
    >
      <IconButton
        label="Åbn menu"
        icon={<Menu />}
        className="lg:hidden"
        onClick={() => setMobileNavOpen(true)}
      />

      <Breadcrumbs items={breadcrumbs} className="hidden min-w-0 flex-1 sm:block" />
      <div className="flex-1 sm:hidden" />

      {/* Søgefeltet er en knap, ikke et input: den åbner kommandopaletten,
          hvor selve søgningen foregår. */}
      <button
        type="button"
        onClick={() => setCommandPaletteOpen(true)}
        className={cn(
          'hidden items-center gap-2 rounded-[var(--radius-control)] md:flex',
          'h-9 w-64 border border-line-subtle bg-app-900/60 px-3',
          'text-[13px] text-text-muted transition-colors',
          'hover:border-line-strong hover:text-text-secondary',
          'focus-visible:outline-2 focus-visible:outline-brand-400',
        )}
      >
        <Search className="size-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">Søg i BygOverblik…</span>
        <kbd className="rounded border border-line-subtle bg-overlay-soft px-1.5 py-0.5 font-mono text-[10px]">
          ⌘K
        </kbd>
      </button>

      <IconButton
        label="Søg"
        icon={<Search />}
        className="md:hidden"
        onClick={() => setCommandPaletteOpen(true)}
      />

      <OrganizationSwitcher />

      <Button variant="primary" size="sm" leadingIcon={<Plus />} className="hidden sm:inline-flex" asChild>
        <Link to="/app/projects/new">Nyt projekt</Link>
      </Button>

      <ThemeToggle />

      <NotificationBell />

      {/* IconButton kan ikke være `asChild`: Slot ville gøre selve ikonet til
          roden. Knappen bygges derfor direkte af Button omkring linket. */}
      <Button
        variant="ghost"
        size="icon"
        className="hidden sm:inline-flex"
        aria-label="Hjælp"
        title="Hjælp"
        asChild
      >
        <Link to="/app/hjaelp">
          <CircleHelp />
        </Link>
      </Button>

      <UserMenu />
    </header>
  )
}

/**
 * Månen: skifter mellem lyst og mørkt tema.
 *
 * Ikonet viser det, klikket fører til — måne i lyst tema, sol i mørkt — og
 * ikke den tilstand, man allerede er i. Etiketten siger det samme med ord, så
 * en skærmlæser ikke skal gætte, hvad et ikon betyder.
 */
function ThemeToggle() {
  const theme = useUiStore((state) => state.theme)
  const setTheme = useUiStore((state) => state.setTheme)
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated')

  const goingDark = theme === 'light'
  const label = goingDark ? 'Skift til mørkt tema' : 'Skift til lyst tema'

  function toggle() {
    const next = goingDark ? 'dark' : 'light'

    // Fladen skifter med det samme. Serveren får besked bagefter, og et
    // mislykket kald må ikke rulle skiftet tilbage for øjnene af brugeren:
    // valget gælder stadig i denne browser, det er kun rejsen til næste enhed,
    // der går tabt.
    setTheme(next)

    if (isAuthenticated) {
      void authApi.updateTheme(next).catch(() => undefined)
    }
  }

  return (
    <IconButton
      label={label}
      icon={goingDark ? <Moon /> : <Sun />}
      onClick={toggle}
      aria-pressed={theme === 'dark'}
    />
  )
}

/**
 * Skift mellem de organisationer, brugeren er medlem af.
 *
 * Skiftet sker på serveren: organisationen ligger i access tokenets claims, så
 * der skal udstedes et nyt token. Vises kun når der er noget at skifte mellem.
 */
function OrganizationSwitcher() {
  const user = useAuthStore((state) => state.user)
  const switchOrganization = useAuthStore((state) => state.switchOrganization)

  const mutation = useMutation({
    mutationFn: switchOrganization,
    onSuccess: () => toast.success('Organisation skiftet'),
    onError: (error) => toast.fromError(error, 'Kunne ikke skifte organisation.'),
  })

  const active = user?.activeOrganization

  if (!user || !active) {
    return null
  }

  const hasMultiple = user.organizations.length > 1

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={!hasMultiple}>
        <Button
          variant="ghost"
          size="sm"
          className="hidden lg:inline-flex"
          isLoading={mutation.isPending}
          aria-label={
            hasMultiple ? `Aktiv organisation: ${active.name}. Skift organisation` : active.name
          }
        >
          <Building2 className="text-text-muted" aria-hidden="true" />
          <span className="max-w-40 truncate">{active.name}</span>
          {hasMultiple && <ChevronDown className="text-text-muted" aria-hidden="true" />}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuLabel>Organisationer</DropdownMenuLabel>

        {user.organizations.map((organization) => (
          <DropdownMenuItem
            key={organization.id}
            onSelect={() => {
              if (organization.id !== active.id) {
                mutation.mutate(organization.id)
              }
            }}
          >
            {organization.id === active.id ? (
              <Check className="text-brand-300" aria-hidden="true" />
            ) : (
              <Building2 aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1 truncate">{organization.name}</span>
            <Badge tone="neutral" className="shrink-0">
              {ROLE_LABELS[organization.role]}
            </Badge>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function UserMenu() {
  const user = useAuthStore((state) => state.user)
  const signOut = useAuthStore((state) => state.signOut)
  const navigate = useNavigate()

  if (!user) return null

  async function handleSignOut() {
    await signOut()
    void navigate('/login', { replace: true })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Åbn brugermenu"
          className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400"
        >
          <Avatar name={user.fullName} initials={user.initials} src={user.avatarUrl} size="md" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuLabel>{user.fullName}</DropdownMenuLabel>
        <p className="truncate px-2.5 pb-1.5 text-[11px] text-text-muted">{user.email}</p>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to="/app/settings/profile">
            <User aria-hidden="true" />
            Min profil
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/app/settings/security">
            <Settings aria-hidden="true" />
            Sikkerhed
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem destructive onSelect={() => void handleSignOut()}>
          <LogOut aria-hidden="true" />
          Log ud
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
